"use client";

import { JSX, useMemo } from "react";
import { cn } from "@/lib/utils";
import { fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import {
  RING_KPIS_MAX_ITEMS,
  RING_KPIS_TWO_ROW_THRESHOLD,
  type ModuleRingKpisData,
} from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import { formatModuleValue } from "./shared/value-format";
import {
  RING_KPI_RING_GAP_DEG as RING_GAP_DEG,
  RING_KPI_RING_SEGMENTS as RING_SEGMENTS,
  RING_KPI_RING_SIZE as RING_SIZE,
  RING_KPI_RING_THICKNESS as RING_THICKNESS,
  RING_KPI_TILE_MIN_WIDTH as TILE_MIN_WIDTH,
} from "@/config/layout";

type DeltaTone = "good" | "bad" | "flat";

const r3 = (n: number) => Math.round(n * 1000) / 1000;

function polar(c: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: r3(c + r * Math.cos(rad)), y: r3(c + r * Math.sin(rad)) };
}

function arc(c: number, r: number, from: number, to: number) {
  const a = polar(c, r, from);
  const b = polar(c, r, to);
  return `M ${a.x} ${a.y} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${b.x} ${b.y}`;
}

const RING_PATHS = (() => {
  const c = RING_SIZE / 2;
  const r = c - RING_THICKNESS / 2;
  const step = 360 / RING_SEGMENTS;
  return Array.from({ length: RING_SEGMENTS }, (_, i) =>
    arc(c, r, i * step + RING_GAP_DEG / 2, (i + 1) * step - RING_GAP_DEG / 2),
  );
})();

function deltaTone(delta: number, invert?: boolean): DeltaTone {
  if (Math.abs(delta) < 0.0005) return "flat";
  return delta > 0 !== Boolean(invert) ? "good" : "bad";
}

function toneColor(tone: DeltaTone, grayscale: boolean | undefined, flat: string): string {
  if (tone === "flat") return flat;
  if (grayscale) return "var(--fg)";
  return tone === "good" ? "var(--emerald)" : "var(--amber)";
}

function SegmentedRing({ value, label, color }: { value: number; label: string; color: string }) {
  const fraction = Math.min(1, Math.max(0, value / 100));
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value)}
      className="relative inline-flex shrink-0"
      style={{ width: RING_SIZE, height: RING_SIZE }}
    >
      <svg
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        className="absolute inset-0 size-full"
        aria-hidden
      >
        {RING_PATHS.map((d, i) => {
          const lit = (i + 0.5) / RING_SEGMENTS <= fraction;
          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={lit ? color : "var(--track)"}
              strokeWidth={RING_THICKNESS}
              style={{ transition: "stroke 0.3s" }}
            />
          );
        })}
      </svg>
    </div>
  );
}

function DeltaText({
  delta,
  period,
  tone,
  color,
}: {
  delta: number;
  period: string;
  tone: DeltaTone;
  color: string;
}) {
  const { intlLocale } = useViewerLanguage();
  const sign = delta > 0 ? "+" : delta < 0 ? "-" : "±";
  const text = `${sign}${fmtNum(Math.abs(delta) * 100, 1, intlLocale)} %${period ? ` ${period}` : ""}`;
  return (
    <span
      className="flex w-full min-w-0 items-center gap-1 text-xs tabular-nums"
      style={{ color }}
    >
      {tone !== "flat" && (
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden
          className="shrink-0"
          style={{ transform: delta < 0 ? "rotate(180deg)" : undefined }}
        >
          <path
            d="M5 1.5v7M2 4.5 5 1.5l3 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      <span className="min-w-0 truncate">{text}</span>
    </span>
  );
}

export function ringKpiGrid(count: number): { rows: number; cols: number } {
  const n = Math.max(1, count);
  const rows = n >= RING_KPIS_TWO_ROW_THRESHOLD ? 2 : 1;
  return { rows, cols: Math.ceil(n / rows) };
}

export default function ModuleRingKpis({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleRingKpisData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const items = useMemo(
    () => (data?.items ?? []).slice(0, RING_KPIS_MAX_ITEMS),
    [data?.items],
  );
  const { t, intlLocale } = useViewerLanguage();
  const previousLabel = stripInlineMarkdown(data?.previousLabel) || t("Previous");
  const deltaPeriod = stripInlineMarkdown(data?.deltaPeriod);
  const { rows } = ringKpiGrid(items.length);
  const anyPrevious = items.some((m) => m.previous !== undefined);

  const tile = useHoverSelect<number>();
  const hasHover = useHoverCapable();

  return (
    <div
      className={cn(
        "w-full min-w-0 snap-x snap-mandatory overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      style={importsTokenScope(grayscale, accent)}
    >
      <div
        className="grid gap-2"
        style={{
          gridAutoFlow: "column",
          gridTemplateRows: `repeat(${rows}, auto)`,
          gridAutoColumns: `minmax(${TILE_MIN_WIDTH}, 1fr)`,
        }}
      >
        {items.map((m, i) => {
          const fmt = (v: number) =>
            formatModuleValue(v, {
              format: m.format,
              digits: m.digits,
              unit: m.unit,
              locale: intlLocale,
            });
          const progress = m.progress ?? (m.max ? (m.value / m.max) * 100 : 0);
          const label = stripInlineMarkdown(m.label);
          const delta =
            m.previous !== undefined && m.previous !== 0 ? m.value / m.previous - 1 : undefined;
          const tone: DeltaTone = delta === undefined ? "flat" : deltaTone(delta, m.invertDelta);
          const ringColor = toneColor(tone, grayscale, "var(--chart-2)");
          const dim = tile.effective !== null && tile.effective !== i;
          return (
            <button
              key={m.id ?? i}
              type="button"
              aria-pressed={tile.selected === i}
              className={cn(
                "flex min-w-0 snap-start flex-col gap-4 rounded-xl border border-[var(--border)] p-5 text-left transition-opacity duration-150",
                dim && "opacity-40",
              )}
              onMouseEnter={hasHover ? () => tile.setHovered(i) : undefined}
              onMouseLeave={hasHover ? () => tile.setHovered(null) : undefined}
              onFocus={hasHover ? () => tile.setHovered(i) : undefined}
              onBlur={hasHover ? () => tile.setHovered(null) : undefined}
              onClick={(e) =>
                tile.select(i, {
                  kind: "card",
                  id: m.id ?? i,
                  index: i,
                  element: e.currentTarget,
                })
              }
            >
              <span className="block w-full truncate text-sm text-[var(--muted)]">{label}</span>
              <span className="flex w-full min-w-0 items-center gap-3">
                <SegmentedRing value={progress} label={label} color={ringColor} />
                <span className="min-w-0 truncate text-2xl font-medium tracking-tight tabular-nums text-[var(--fg)]">
                  {fmt(m.value)}
                </span>
              </span>
              {anyPrevious &&
                (m.previous !== undefined ? (
                  <span className="flex w-full min-w-0 flex-col gap-0.5">
                    {delta !== undefined ? (
                      <DeltaText
                        delta={delta}
                        period={deltaPeriod}
                        tone={tone}
                        color={toneColor(tone, grayscale, "var(--muted)")}
                      />
                    ) : (
                      <span className="invisible text-xs" aria-hidden>
                        &nbsp;
                      </span>
                    )}
                    <span className="block w-full truncate text-xs tabular-nums text-[var(--subtle)]">
                      {previousLabel} {fmt(m.previous)}
                    </span>
                  </span>
                ) : (
                  <span className="invisible flex flex-col gap-0.5" aria-hidden>
                    <span className="text-xs">&nbsp;</span>
                    <span className="text-xs">&nbsp;</span>
                  </span>
                ))}
            </button>
          );
        })}
      </div>
    </div>
  );
}
